import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { chartColors } from '@src/shared/ui/components/Chart/includes/chartColors';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import { isEmpty, isUndefined, shuffle } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Scatter } from 'react-chartjs-2';

import { getTooltipFooter, getTooltipLabel, getTooltipTitle } from '../../includes/functions';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

/**
 * ScatterChart
 *
 * A scatter chart is a graphical representation of data points plotted on a two-dimensional coordinate system,
 * typically used to display relationships between two variables.
 *
 * @param legendLimit
 * @param showLegend
 * @param data
 * @returns {React.JSX.Element|`\nValue: ${string}\nPercentage: ${*}%\n`|*}
 * @constructor
 */
export function ScatterChart({ data = [], legendLimit = 10, showLegend = true }) {
  const [datasets, setDatasets] = useState([]);

  /**
   * Defines an array of colors and patterns to use for rendering the datasets.
   *
   * @type {Array<CanvasPattern>}
   */
  const patterns = useMemo(() => {
    return shuffle(chartColors).map((colorName) => {
      return themeConfiguration?.theme?.colors?.[`${colorName}`];
    });
  }, [themeConfiguration]);

  /**
   * Configure the labels and datasets to be used by the chart.
   */
  useEffect(() => {
    if (!isEmpty(data)) {
      const theDataset = [];

      data?.forEach((item) => {
        const dataset = {
          backgroundColor: patterns,
          data: [
            {
              x: item?.value,
              y: item?.yvalue,
            },
          ],
          label: item?.name,
        };

        theDataset.push(dataset);
      });

      if (theDataset !== datasets) {
        setDatasets(theDataset);
      }
    }
  }, [data]);

  return (
    <ErrorHandler location="src/shared/ui/components/Chart/components/ScatterChart/ScatterChart.jsx">
      <Scatter
        data={{
          datasets,
        }}
        options={{
          animation: {
            duration: 0, // general animation time
          },
          hover: {
            animationDuration: 0, // duration of animations when hovering an item
          },
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: isUndefined(showLegend) ? !isUndefined(legendLimit) && datasets.length <= legendLimit : true,
            },
            tooltip: {
              callbacks: {
                footer(args) {
                  return getTooltipFooter(args, data);
                },
                label(args) {
                  return getTooltipLabel(args, data);
                },
                title(args) {
                  return getTooltipTitle(args, data);
                },
              },
            },
          },
          responsive: true,
          responsiveAnimationDuration: 0, // animation duration after a resize
        }}
      />
    </ErrorHandler>
  );
}
